import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { BRAND } from "@/constants/brand";
import { SiteFooter as PublicFooter, SiteNav as PublicNav } from "@/components/media/SiteNav";
import { Eyebrow } from "@/components/ladder/Offers";
import { LeadForm } from "@/components/ladder/LeadForm";
import { Check } from "lucide-react";

const CONFIG = {
  smash_academy: {
    source: "smash_academy_waitlist",
    eyebrow: "SMASH ACADEMY WAITLIST",
    title: "Get in line for Smash Academy.",
    blurb:
      "Smash Academy is our complete system for building, operating, marketing and growing an event-based beverage business. Tell us what you're building and we'll bring you in first.",
    fields: ["first_name", "last_name", "email", "phone", "city", "state", "goal"],
    submit: "JOIN THE SMASH ACADEMY WAITLIST",
    done: "You're on the list. We'll reach out before enrollment opens.",
  },
  popup_profits: {
    source: "popup_profits_waitlist",
    eyebrow: "SMASH ACADEMY WAITLIST",
    title: "Founding member access is closed right now.",
    blurb:
      "Smash Academy opens in groups so we can actually help people launch. Add your name and you'll hear from us when the next founding group opens.",
    fields: ["first_name", "email", "goal"],
    submit: "JOIN THE WAITLIST",
    done: "You're on the list for the next Smash Academy founding group.",
  },
  popup_operator: {
    source: "operator_application",
    eyebrow: "SMASH POP-UP OPERATOR",
    title: "Apply to explore operating with Smash.",
    blurb:
      "This is an application and qualification process, not a checkout. Tell us about yourself and where you'd want to operate.",
    fields: ["first_name", "last_name", "email", "phone", "city", "state", "goal"],
    submit: "SUBMIT MY APPLICATION",
    done: "Application received. We review these personally and will be in touch.",
  },
  trailer_operator: {
    source: "operator_application",
    eyebrow: "SMASH TRAILER OPERATOR",
    title: "Apply for trailer opportunities.",
    blurb:
      "Trailer operator opportunities are under development and require qualification. Start with an application and we'll take it from there.",
    fields: ["first_name", "last_name", "email", "phone", "city", "state", "goal"],
    submit: "APPLY FOR TRAILER OPPORTUNITIES",
    done: "Application received. We review these personally and will be in touch.",
  },
};

export default function LeadCapture() {
  const { productKey } = useParams();
  const config = CONFIG[productKey];
  const [product, setProduct] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [disclaimer, setDisclaimer] = useState("");

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProduct((data.products || []).find((p) => p.key === productKey) || null);
      setDisclaimer(data.disclaimer || "");
    });
  }, [productKey]);

  if (!config)
    return (
      <div data-testid="lead-capture-unknown">
        <PublicNav />
        <div className="mx-auto max-w-lg px-5 py-24 text-center">
          <h1 className="neon text-4xl">Nothing here yet</h1>
          <p className="mt-4 text-sm text-muted-foreground">That offer isn't open for signup.</p>
        </div>
        <PublicFooter />
      </div>
    );

  const isOperator = config.source === "operator_application";

  return (
    <div data-testid={`lead-capture-${productKey}`}>
      <PublicNav />
      <div className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 photo-band opacity-20"
          style={{ backgroundImage: `url(${BRAND.photos.graffitiColor})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/90" />
        <div className="relative mx-auto max-w-3xl px-5 py-16 md:px-10 md:py-20">
          <Eyebrow>{config.eyebrow}</Eyebrow>
          <h1 className="neon mt-4 text-4xl leading-[0.98] sm:text-5xl">{config.title}</h1>
          <p className="mt-5 text-base text-muted-foreground md:text-lg">{config.blurb}</p>
        </div>
      </div>

      <div className="mx-auto grid max-w-5xl gap-10 px-5 py-14 md:grid-cols-2 md:px-10 md:py-20">
        <div>
          {product?.features?.length ? (
            <>
              <div className="mono text-[10px] tracking-widest text-muted-foreground">
                WHAT THIS COVERS
              </div>
              <ul className="mt-3 space-y-1.5">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check size={14} className="mt-0.5 shrink-0 text-primary" />
                    {f}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {isOperator && disclaimer ? (
            <p
              data-testid="apply-disclaimer"
              className="mono mt-8 border-l-2 border-border pl-4 text-[11px] leading-relaxed text-muted-foreground"
            >
              {disclaimer}
            </p>
          ) : null}
        </div>

        <div className="border border-border bg-[#141414] p-6 md:p-8">
          {submitted ? (
            <div data-testid="lead-capture-success" className="text-center">
              <p className="display neon text-2xl md:text-3xl">{config.done}</p>
            </div>
          ) : (
            <LeadForm
              source={config.source}
              productKey={productKey}
              fields={config.fields}
              submitLabel={config.submit}
              testId="capture-form"
              onDone={() => setSubmitted(true)}
            />
          )}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
